import { useCallback, useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { any, object, string, TypeOf, z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import {
  saveCompany,
  updateCompany,
} from "../../redux/actions/company.actions";
import { useDispatch, useSelector } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import SaveIcon from "@mui/icons-material/Save";
import { green } from "@mui/material/colors";
import {
  Box,
  Button,
  TextField,
  FormControl,
  FormHelperText,
  useTheme,
  Select,
  InputLabel,
  MenuItem,
} from "@mui/material";
import Mapbox from "../../components/Locationmap/Mapbox";
import MapContext from "../../components/Locationmap/MapContext";
import GooglePlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-google-places-autocomplete";

import { storeStateType } from "../../redux/store";

export type baseStyleType = {
  root?: string;
  disabled?: string;
  focused?: string;
  error?: string;
};
export type selectAddressOptionType = {
  label: string;
  value: {
    description: string;
    matched_substrings: string[];
    place_id: string;
    reference: string;
    structured_formatting: {
      main_text: string;
      main_text_matched_substrings: string[];
      secondary_text: string;
    };
    terms: string[];
    types: string[];
  };
};

interface Props {
  isDialogOpened: boolean;
  handleCloseDialog: any;
  action: string;
  filterData: any;
}

const formSchema = object({
  id: any(),
  facility_name: z.string().nonempty("Facility name is required"),
  facility_type: z.string().nullable(),
  facility_address: string().nonempty("Facility address is required"),
  facility_contact_email: string()
    .nonempty("Facility email is required")
    .regex(/^(\s?[^\s,]+@[^\s,]+\.[^\s,]+\s?,)*(\s?[^\s,]+@[^\s,]+\.[^\s,]+)$/),
  facility_contact_number: string().nonempty(
    "Facility contact number is required"
  ),
  facility_contact_person: string(),
  facility_latlang: z
    .string()
    .nonempty("Facility Lat/Lang coordinats are required")
    .regex(/^(-?\d+(\.\d+)?)(,\s*(-?\d+(\.\d+)?))$/, "Invalid Lag Lang"),

  //logo: z.any(z.instanceof(File)).optional(),
  facility_status: any(),
});
type FormInput = TypeOf<typeof formSchema>;

const defaultValues = {
  facility_name: "",
  facility_address: "",
  facility_contact_email: "",
  facility_contact_number: "",
  facility_latlang: "",
  //logo: ""
  facility_contact_person: "",
};

export function FacilityForm(props: Props) {
  const { handleCloseDialog } = props;
  const dispatch = useDispatch();

  const theme = useTheme();
  const { company, save } = useSelector(
    (state: storeStateType) => state.company
  );
  const loading = useSelector((state: storeStateType) => state.UI.loading);

  const {
    register,
    reset,
    control,
    getValues,
    formState: { errors },
    handleSubmit,
  } = useForm<FormInput>({ resolver: zodResolver(formSchema) });
  // let imagePath: string | undefined = process.env.REACT_APP_ENDPOINT;
  // imagePath = imagePath ? imagePath.replace(/\/+$/, "") : "";
  const [latlng, setLatlng] = useState(null);

  const handleClose = useCallback(() => {
    handleCloseDialog(false);
    reset(defaultValues);
  }, [handleCloseDialog, reset]);

  useEffect(() => {
    if (save) {
      handleClose();
    }
  }, [save, handleClose]);

  useEffect(() => {
    reset(company ?? {});
  }, [company, reset]);

  useEffect(() => {
    if (latlng) {
      reset({ ...getValues(), facility_latlang: latlng });
    }
  }, [latlng, reset, getValues]);

  const onFormSubmit: SubmitHandler<FormInput> = (data) => {
    const formData = new FormData();
    formData.append("facility_name", data.facility_name);
    formData.append("facility_address", data.facility_address);
    formData.append("facility_contact_email", data.facility_contact_email);
    formData.append("facility_contact_number", data.facility_contact_number);
    formData.append("facility_contact_person", data.facility_contact_person);
    formData.append("facility_latlang", data.facility_latlang);
    if (data.facility_type)
      formData.append("facility_type", data.facility_type);

    if (data.id) {
      formData.append("facility_status", data.facility_status);
      dispatch<any>(updateCompany(formData, data.id, props.filterData));
    } else {
      formData.append("facility_status", 1 as unknown as string);
      dispatch<any>(saveCompany(formData, props.filterData));
    }
  };

  const [isOpen, setIsOpen] = useState(false);
  const [handelaction] = useState("");
  const handleClick = (event: any) => {
    setIsOpen(!isOpen);
  };

  const customStyles = !errors["facility_name"]
    ? {
        control: (baseStyles: baseStyleType) => ({
          ...baseStyles,
          backgroundColor: "transparent",
          borderTop: 0,
          borderLeft: 0,
          borderRight: 0,
          borderRadius: 0,
          // borderColor: "rgba(0, 0, 0, 0.87)",
          marginTop: "11px",
          boxShadow: 0,
        }),
        container: (provided: baseStyleType) => ({
          ...provided,
          background: "transparent",
          color: "black",

          // Add your container styles here
        }),
        input: (provided: baseStyleType) => ({
          ...provided,
          marginLeft: "0px",
          padding: 0,
          margin: 0,
          color: theme?.palette?.text?.primary,
        }),
        dropdownContainer: (provided: baseStyleType) => ({
          ...provided,
          // Add your dropdown container styles here
        }),
        dropdownItem: (provided: baseStyleType) => ({
          ...provided,

          // Add your dropdown item styles here
        }),
        singleValue: (provided: baseStyleType) => ({
          ...provided,
          marginLeft: "0px",
          padding: 0,
          color: theme?.palette?.text?.primary, //selected text color
        }),
        item: (provided: baseStyleType) => ({
          ...provided,
          marginLeft: "0px",
          padding: 0,
        }),
      }
    : {
        control: (provided: baseStyleType) => ({
          ...provided,
          borderTop: 0,
          borderLeft: 0,
          borderRight: 0,
          borderRadius: 0,
          borderColor: "error",
        }),
        input: (provided: baseStyleType) => ({
          ...provided,
          marginLeft: "0px",
          padding: 0,
        }),
      };

  const facilityTypeOptions = [
    { name: "Hospitals", id: "hospitals" },
    { name: "Aged Care", id: "agedcare" },
    { name: "Others", id: "others" },
  ];

  return (
    <MapContext.Provider value={{ latlng, setLatlng }}>
      <Dialog open={props.isDialogOpened} onClose={handleClose} fullWidth>
        <DialogTitle>{props.action}</DialogTitle>
        <DialogContent>
          <Box
            component="form"
            id="companyform"
            name="companyform"
            noValidate
            onSubmit={handleSubmit(onFormSubmit)}
          >
            <TextField
              autoFocus
              margin="dense"
              id="facility_name"
              label="Facility name"
              fullWidth
              variant="standard"
              required
              {...register("facility_name")}
              error={!!errors["facility_name"]}
              helperText={
                errors["facility_name"] ? errors["facility_name"].message : ""
              }
            />
            <FormControl
              fullWidth
              className="custom_select"
              variant={"standard"}
              sx={{ height: "100%" }}
            >
              <InputLabel id="status-select-label" className="mb-2">
                Facility Type *
              </InputLabel>
              <Controller
                name={"facility_type"}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <>
                    <Select
                      defaultValue={value}
                      labelId={"Facility Type"}
                      className="input-container"
                      id={"facility_type"}
                      size="medium"
                      value={value}
                      onChange={onChange}
                      sx={{ textAlign: "left" }}
                    >
                      {facilityTypeOptions.map((option: any, index: number) => (
                        <MenuItem key={`option-${index}`} value={option.id}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </Select>

                    <FormHelperText error={!!errors["facility_type"]}>
                      {errors["facility_type"]
                        ? errors["facility_type"].message
                        : ""}
                    </FormHelperText>
                  </>
                )}
              />
            </FormControl>

            <Box className="facility-address-field">
              <FormControl
                fullWidth
                variant="standard"
                error={!!errors["facility_address"]}
              >
                {/* <InputLabel htmlFor="facility_address">
                  Address
                </InputLabel> */}

                <Controller
                  control={control}
                  name="facility_address"
                  render={({ field: { onChange, value } }) => (
                    <GooglePlacesAutocomplete
                      apiKey={process.env.REACT_APP_GOOGLE_AUTOCOMPLETE_KEY}
                      selectProps={{
                        placeholder: `Facility Address*`,
                        defaultInputValue: value,
                        isClearable: true,
                        onChange: (address: selectAddressOptionType | null) => {
                          if (address) {
                            geocodeByAddress(address.value.description)
                              .then((results) => getLatLng(results[0]))
                              .then(({ lat, lng }) => {
                                const geoCorindate = lat + "," + lng;
                                reset({
                                  ...getValues(),
                                  facility_address: address.value.description,
                                  facility_latlang: geoCorindate,
                                });
                                onChange(address.value.description);
                              });
                          }
                        },
                        styles: customStyles,
                      }}
                    />
                  )}
                />
                <FormHelperText id="facility_address_errror_message">
                  {errors["facility_address"]
                    ? errors["facility_address"].message
                    : ""}
                </FormHelperText>
              </FormControl>
            </Box>

            <TextField
              margin="dense"
              id="facility_contact_number"
              label="Contact Number"
              fullWidth
              variant="standard"
              required
              {...register("facility_contact_number")}
              error={!!errors["facility_contact_number"]}
              helperText={
                errors["facility_contact_number"]
                  ? errors["facility_contact_number"].message
                  : ""
              }
            />
            <TextField
              margin="dense"
              id="facility_contact_email"
              label="Email Address"
              fullWidth
              variant="standard"
              required
              {...register("facility_contact_email")}
              error={!!errors["facility_contact_email"]}
              helperText={
                errors["facility_contact_email"]
                  ? errors["facility_contact_email"].message
                  : ""
              }
            />

            <TextField
              margin="dense"
              id="facility_contact_person"
              label="Contat Person"
              fullWidth
              variant="standard"
              {...register("facility_contact_person")}
              error={!!errors["facility_contact_person"]}
              helperText={
                errors["facility_contact_person"]
                  ? errors["facility_contact_person"].message
                  : ""
              }
              InputLabelProps={{
                shrink: true,
              }}
            />

            <TextField
              margin="dense"
              id="facility_latlang"
              label="Lat/Lang"
              fullWidth
              variant="standard"
              required
              {...register("facility_latlang")}
              error={!!errors["facility_latlang"]}
              helperText={
                errors["facility_latlang"]
                  ? errors["facility_latlang"].message
                  : ""
              }
              onClick={handleClick}
              InputLabelProps={{
                shrink: true,
              }}
            />

            <DialogActions>
              <Box sx={{ m: 1, position: "relative" }}>
                <Button
                  type="submit"
                  form="companyform"
                  variant="contained"
                  color="success"
                  startIcon={<SaveIcon />}
                  disabled={loading}
                >
                  Save
                </Button>
                {loading && (
                  <CircularProgress
                    size={24}
                    sx={{
                      color: green[500],
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      marginTop: "-12px",
                      marginLeft: "-12px",
                    }}
                  />
                )}
              </Box>
              <Button
                onClick={handleClose}
                variant="contained"
                color="error"
                disabled={loading}
              >
                Cancel
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>
      <Mapbox
        isDialogOpened={isOpen}
        handleCloseDialog={() => setIsOpen(false)}
        action={handelaction}
      />
    </MapContext.Provider>
  );
}
